import React from 'react';
import PropTypes from 'prop-types';
import { Box, Typography } from '@mui/material';
import InboxOutlinedIcon from '@mui/icons-material/InboxOutlined';

/**
 * Empty state component displayed when no data is available.
 * Use `size="compact"` inside stacked form widgets (e.g. TableWidgetV2).
 */
const EmptyState = ({
  title = 'Nenhum resultado encontrado',
  message = 'Tente ajustar os filtros para encontrar o que procura.',
  onClearFilters = null,
  size = 'default',
}) => {
  const isCompact = size === 'compact';
  const resolvedTitle = title || 'Nenhum resultado encontrado';

  return (
    <Box
      role="status"
      aria-live="polite"
      sx={
        isCompact
          ? {
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1.5,
              py: 2,
              px: 2,
              textAlign: 'left',
            }
          : {
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              p: 4,
              minHeight: 300,
              textAlign: 'center',
            }
      }
    >
      <InboxOutlinedIcon
        aria-hidden="true"
        sx={{
          fontSize: isCompact ? 28 : 64,
          color: 'grey.400',
          flexShrink: 0,
          ...(isCompact ? {} : { mb: 2 }),
        }}
      />
      <Box
        sx={
          isCompact
            ? { minWidth: 0 }
            : { display: 'flex', flexDirection: 'column', alignItems: 'center' }
        }
      >
        <Typography
          variant={isCompact ? 'body2' : 'h6'}
          sx={{
            color: 'text.secondary',
            ...(isCompact ? {} : { mb: 1 }),
          }}
        >
          {resolvedTitle}
        </Typography>
        {message && (
          <Typography
            variant={isCompact ? 'caption' : 'body2'}
            sx={{
              color: 'text.secondary',
              maxWidth: 400,
              display: 'block',
            }}
          >
            {message}
          </Typography>
        )}
        {onClearFilters && (
          <Typography
            variant={isCompact ? 'caption' : 'body2'}
            role="button"
            tabIndex={0}
            onClick={onClearFilters}
            onKeyPress={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onClearFilters();
              }
            }}
            sx={{
              color: 'text.secondary',
              maxWidth: 400,
              mt: isCompact ? 0.5 : 2,
              cursor: 'pointer',
              textDecoration: 'underline',
              display: 'block',
            }}
          >
            Limpar filtros
          </Typography>
        )}
      </Box>
    </Box>
  );
};

EmptyState.propTypes = {
  title: PropTypes.string,
  message: PropTypes.string,
  onClearFilters: PropTypes.func,
  size: PropTypes.oneOf(['default', 'compact']),
};

export default EmptyState;
